import axios from "axios";

import { validateIp } from "./validateIp";

/** One probe result from the client-side IP-discovery port walk. */
export interface DiscoveredIp {
  Port: number;
  IP: string;
}

// A closed/filtered port otherwise hangs until the OS TCP timeout.
export const DISCOVERY_PROBE_TIMEOUT_MS = 3500;
export const MAX_DISCOVERY_CONCURRENCY = 10;

// How many times the background walk probes each host×port. A client behind
// multi-WAN (ECMP) or a large CGNAT pool can egress different flows via different
// public IPs; repeated samples surface those (unioned, deduped by IP). Needs the
// host to close each connection (Connection: close) so a repeat takes a fresh flow.
export const DISCOVERY_SAMPLES_PER_TARGET = 4;

/**
 * A probe's outcome. Some failures are deterministic and named exactly
 * ("blocked-port", "mixed-content"). For the rest, the browser hides whether a
 * cross-origin failure is CORS, DNS, TLS, or connection-refused, so they
 * collapse into "network-error".
 */
export type DiscoveryProbeStatus =
  | "pending"
  | "ok"
  | "timeout"
  | "http-error"
  | "network-error"
  | "blocked-port"
  | "mixed-content"
  | "unexpected-response";

export interface DiscoveryProbeResult {
  Port: number;
  /** The discovery host this probe hit. Set by the multi-URL walk. */
  BaseUrl?: string;
  /** Set only when status is "ok". */
  IP?: string;
  status: DiscoveryProbeStatus;
  /** Set only when status is "http-error". */
  httpStatus?: number;
}

/**
 * The discovery base URLs to probe, from a system-status response. Prefers the
 * IPApis list (single host in single mode, per-family hosts in split mode) and
 * falls back to the legacy single IPApi field for older servers.
 */
export function discoveryUrlsFromStatus(data: { IPApis?: string[]; IPApi?: string }): string[] {
  const urls = data.IPApis?.filter((url) => url) ?? [];
  if (urls.length > 0) return urls;
  return data.IPApi ? [data.IPApi] : [];
}

/**
 * The discovery URL mode (single vs split) to show for a saved settings
 * response. Honours an explicit DiscoverIPSplitURLs; when unset (pre-feature
 * installs) keeps single mode if a single URL is saved, so it isn't hidden and
 * cleared on the next save. True greenfield defaults to split.
 */
export function discoveryUrlModeFromSettings(data: {
  DiscoverIPSplitURLs?: boolean | null;
  DiscoverIPURL?: string | null;
}): "single" | "split" {
  if (data.DiscoverIPSplitURLs === true) return "split";
  if (data.DiscoverIPSplitURLs === false) return "single";
  return data.DiscoverIPURL ? "single" : "split";
}

/**
 * Probes one port on the discovery host and classifies the outcome. Never
 * rejects: every failure maps to a status. Uses raw axios because the host is
 * cross-origin (not the shared /api instance); responseType "text" pins the
 * plain-text-IP contract.
 */
export async function probeDiscoveryPort(baseUrl: string, port: number): Promise<DiscoveryProbeResult> {
  const base = baseUrl.replace(/\/*$/, "");
  // Failures the browser refuses before the request leaves: classify them
  // exactly instead of surfacing an opaque network error.
  if (isBrowserBlockedPort(port)) {
    return { Port: port, status: "blocked-port" };
  }
  if (base.startsWith("http://") && typeof window !== "undefined" && window.location?.protocol === "https:") {
    return { Port: port, status: "mixed-content" };
  }
  try {
    const response = await axios.get(base + ":" + port, {
      timeout: DISCOVERY_PROBE_TIMEOUT_MS,
      responseType: "text",
    });
    const ip = String(response.data).trim();
    if (validateIp(ip)) {
      return { Port: port, IP: ip, status: "ok" };
    }
    return { Port: port, status: "unexpected-response" };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return { Port: port, status: "http-error", httpStatus: error.response.status };
      }
      if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
        return { Port: port, status: "timeout" };
      }
    }
    return { Port: port, status: "network-error" };
  }
}

/**
 * Walks every configured port and returns a result for each, failures included
 * (unlike validDiscoveredIps), so the caller can show what went wrong on each.
 * Preserves input order. onResult fires as each probe settles so the caller can
 * render rows progressively rather than waiting for the whole walk.
 */
export async function walkDiscoveryPorts(
  baseUrl: string,
  ports: number[],
  onResult?: (result: DiscoveryProbeResult, index: number) => void,
): Promise<DiscoveryProbeResult[]> {
  const settled = await mapWithConcurrency(ports, MAX_DISCOVERY_CONCURRENCY, async (port, index) => {
    const result = await probeDiscoveryPort(baseUrl, port);
    onResult?.(result, index);
    return result;
  });
  // probeDiscoveryPort never rejects, so the fallback is unreachable in practice.
  return settled.map((result, i) =>
    result.status === "fulfilled" ? result.value : { Port: ports[i], status: "network-error" as const },
  );
}

/**
 * Walks every (baseUrl, port) pair, probing each `samplesPerTarget` times
 * through one throttled pool (respects the myip host's per-source rate limit).
 * Order is url-major, port-mid, sample-minor, so a target's samples dispatch
 * concurrently over separate connections. onResult fires as each probe settles.
 */
export async function walkDiscoveryUrls(
  baseUrls: string[],
  ports: number[],
  onResult?: (result: DiscoveryProbeResult, index: number) => void,
  samplesPerTarget = 1,
): Promise<DiscoveryProbeResult[]> {
  const samples = Math.max(1, Math.floor(samplesPerTarget));
  const pairs = baseUrls.flatMap((baseUrl) =>
    ports.flatMap((port) => Array.from({ length: samples }, () => ({ baseUrl, port }))),
  );
  const settled = await mapWithConcurrency(pairs, MAX_DISCOVERY_CONCURRENCY, async ({ baseUrl, port }, index) => {
    const result: DiscoveryProbeResult = { ...(await probeDiscoveryPort(baseUrl, port)), BaseUrl: baseUrl };
    onResult?.(result, index);
    return result;
  });
  return settled.map((result, i) =>
    result.status === "fulfilled"
      ? result.value
      : { Port: pairs[i].port, BaseUrl: pairs[i].baseUrl, status: "network-error" as const },
  );
}

/**
 * A valid TCP port: a whole number in 1-65535. Accepts a string (as the tag
 * input holds) or a number.
 */
export function isValidTcpPort(value: unknown): boolean {
  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}

/**
 * Ports the browser refuses to connect to (ERR_UNSAFE_PORT). The discovery walk
 * runs in the browser, so a probe to any of these can never succeed. Mirrors the
 * ports Chromium enforces, verified against a live Chromium. Excludes port 0
 * (isValidTcpPort handles it) and 4190/6679 (on the WHATWG list but Chromium
 * does not block them).
 */
export const BROWSER_BLOCKED_PORTS: ReadonlySet<number> = new Set([
  1, 7, 9, 11, 13, 15, 17, 19, 20, 21, 22, 23, 25, 37, 42, 43, 53, 69, 77, 79, 87, 95, 101, 102, 103, 104, 109, 110,
  111, 113, 115, 117, 119, 123, 135, 137, 139, 143, 161, 179, 389, 427, 465, 512, 513, 514, 515, 526, 530, 531, 532,
  540, 548, 554, 556, 563, 587, 601, 636, 989, 990, 993, 995, 1719, 1720, 1723, 2049, 3659, 4045, 5060, 5061, 6000,
  6566, 6665, 6666, 6667, 6668, 6669, 6697, 10080,
]);

/** True when the browser would refuse to probe this port. Accepts a string or number. */
export function isBrowserBlockedPort(value: unknown): boolean {
  return BROWSER_BLOCKED_PORTS.has(Number(value));
}

/**
 * Keeps only the settled probes that fulfilled with a valid IP, so a failed
 * request or a malformed/HTML body can neither discard the IPs found on other
 * ports nor 400 the whole submission server-side.
 */
export function validDiscoveredIps(probes: PromiseSettledResult<DiscoveredIp>[]): DiscoveredIp[] {
  return probes.flatMap((probe) => (probe.status === "fulfilled" && validateIp(probe.value.IP) ? [probe.value] : []));
}

/**
 * Runs `fn` over `items` with at most `limit` calls in flight at once, settling
 * each (never rejecting the batch) and preserving input order. Throttles the
 * discovery port walk: probing every port at once trips the myip host's
 * per-source-IP rate limit.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { status: "fulfilled", value: await fn(items[i], i) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }

  const poolSize = Math.min(Math.max(Math.floor(limit), 1), items.length);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));
  return results;
}

/**
 * A stable key for a set of discovered IPs: sorted and de-duplicated on the IP
 * alone (the port is irrelevant to what gets granted). Used to skip re-POSTing
 * /discovered-ips and re-bursting discovery when a walk found nothing new.
 */
export function discoveredIpsKey(ips: DiscoveredIp[]): string {
  return [...new Set(ips.map((entry) => entry.IP))].sort().join(",");
}

/**
 * Builds the IP list to send with a click-to-grant. Always includes the user's
 * primary egress IP (port 0). For a CGNATOpen knoc with discovery enabled it
 * also appends the IPs already found by the background discovery poll, so they
 * go live with this grant. These are cached results; the click does not probe.
 * Returns primary-only otherwise.
 */
export function grantIps(
  currentAddress: string,
  cgnatOpen: boolean,
  discoveryEnabled: boolean,
  discoveredIps: DiscoveredIp[],
): DiscoveredIp[] {
  const ips: DiscoveredIp[] = [{ Port: 0, IP: currentAddress }];
  if (cgnatOpen && discoveryEnabled) {
    ips.push(...discoveredIps);
  }
  return ips;
}
